import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";

const VALID_PLANS = ["connect", "grow", "multiply"];
// Launch: only Connect is purchasable. Grow/Multiply are Coming Soon (no checkout).
const PURCHASABLE_PLANS = ["connect"];
const VALID_INTERVALS = ["monthly", "annual"];
const PLAN_LABEL = { connect: "Connect", grow: "Grow", multiply: "Multiply" };

export default function Signup() {
  const { register, formatApiError, user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const plan = (params.get("plan") || "").toLowerCase();
  const rawInterval = (params.get("interval") || "monthly").toLowerCase();
  const interval = VALID_INTERVALS.includes(rawInterval) ? rawInterval : "monthly";
  // Only Connect proceeds to checkout at launch; grow/multiply params fall back to the billing gate.
  const selectedPlan = PURCHASABLE_PLANS.includes(plan) ? plan : null;
  const [church, setChurch] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const redirecting = useRef(false);

  useEffect(() => {
    // Only auto-redirect a pre-existing session (not the just-registered flow).
    if (user && user.role && !redirecting.current) navigate("/billing", { replace: true });
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    redirecting.current = true;
    try {
      await register(email, password, church);
      // Funnel: send the new church straight into subscription checkout.
      if (selectedPlan) {
        const { data } = await api.post("/billing/checkout", { tier: selectedPlan, interval, origin_url: window.location.origin });
        window.location.href = data.url; // → Stripe Checkout
        return;
      }
      // No plan chosen (e.g. landing "Get Started") → choose one on the billing gate.
      navigate("/billing", { replace: true });
    } catch (err) {
      redirecting.current = false;
      setBusy(false);
      toast.error(formatApiError(err.response?.data?.detail) || "Signup failed");
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/billing";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white rounded-2xl border border-[#E5E2DC] p-8 shadow-sm">
        <Link to="/" className="inline-flex items-center gap-2 mb-6">
          <Logo theme="black" height={30} />
        </Link>
        <h1 className="text-3xl font-bold tracking-tight text-[#2A2626]">Set up your church</h1>
        {selectedPlan ? (
          <p className="mt-1 text-sm text-[#5C5856]" data-testid="signup-plan-note">
            You're subscribing to <strong className="text-[#2A2626]">MinistryHub {PLAN_LABEL[selectedPlan]}</strong>. Next step: secure checkout.
          </p>
        ) : (
          <p className="mt-1 text-sm text-[#5C5856]">Create your account, then choose a plan to go live.</p>
        )}
        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <div>
            <Label htmlFor="church">Church name</Label>
            <Input id="church" required value={church} onChange={(e) => setChurch(e.target.value)} placeholder="Grace Community Church" className="mt-1 h-11" data-testid="signup-church" />
          </div>
          <div>
            <Label htmlFor="email">Admin email</Label>
            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 h-11" data-testid="signup-email" />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 h-11" data-testid="signup-password" />
          </div>
          <Button type="submit" disabled={busy} className="w-full h-11 bg-[#2A2626] hover:bg-black text-white rounded-full" data-testid="signup-submit">
            {busy ? "Creating…" : selectedPlan ? "Continue to checkout" : "Create account"}
          </Button>
          <p className="text-xs text-[#5C5856] text-center leading-relaxed" data-testid="signup-legal-disclosure">
            By creating an account, you agree to our{" "}
            <Link to="/terms" className="text-[#2A2626] font-medium hover:underline" data-testid="signup-terms-link">Terms of Service</Link>{" "}
            and acknowledge our{" "}
            <Link to="/privacy" className="text-[#2A2626] font-medium hover:underline" data-testid="signup-privacy-link">Privacy Policy</Link>.
          </p>
        </form>
        <div className="my-5 flex items-center gap-3 text-xs text-[#5C5856]">
          <div className="flex-1 h-px bg-[#E5E2DC]" /> or <div className="flex-1 h-px bg-[#E5E2DC]" />
        </div>
        <Button onClick={handleGoogle} variant="outline" className="w-full h-11 rounded-full border-[#E5E2DC]" data-testid="signup-google">
          Continue with Google
        </Button>
        <p className="mt-6 text-sm text-[#5C5856] text-center">
          Have an account? <Link to="/login" className="text-[#2A2626] font-medium hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
